import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { useTier } from "./hooks/useTier";
import PublicNav from "./components/PublicNav";
import PublicFooter from "./components/PublicFooter";
import DowngradeConfirmModal from "./components/DowngradeConfirmModal";

const TIERS = [
  {
    key: "free",
    label: "Free",
    price: "$0",
    priceNote: "forever",
    features: [
      "Up to 20 songs",
      "1 saved setlist",
      "Live Mode",
      "Solo use",
    ],
    cta: null,
    ctaLabel: null,
  },
  {
    key: "solo",
    label: "Solo",
    price: "$9",
    priceNote: "/ month",
    features: [
      "Unlimited songs",
      "Unlimited setlists",
      "Live Mode",
      "Merged PDF export",
      "Solo use",
    ],
    envKey: "REACT_APP_STRIPE_SOLO_PRICE_ID",
    ctaLabel: "Upgrade to Solo",
  },
  {
    key: "band",
    label: "Band",
    price: "$15",
    priceNote: "/ month",
    features: [
      "Everything in Solo",
      "Unlimited band members",
      "Shared library & setlists",
      "Role-based permissions",
    ],
    envKey: "REACT_APP_STRIPE_BAND_PRICE_ID",
    ctaLabel: "Upgrade to Band",
  },
];

export default function PricingPage({ tenantContext }) {
  const navigate = useNavigate();
  const tierInfo = useTier(tenantContext ?? null);
  const currentTier = tierInfo.tier;
  const bandId = tenantContext?.bandId ?? null;

  const [loadingTier, setLoadingTier] = useState(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  const handleUpgrade = async (tier) => {
    const priceId = process.env[tier.envKey];
    setLoadingTier(tier.key);
    try {
      const createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
      const result = await createCheckoutSession({ priceId, bandId });
      const url = result.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setLoadingTier(null);
    }
  };

  const isLoading = loadingTier !== null;

  const handlePortalSession = async () => {
    setIsPortalLoading(true);
    try {
      const createPortalSession = httpsCallable(functions, "createPortalSession");
      const result = await createPortalSession({ bandId });
      const url = result.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Portal session error:", err);
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base">
      {/* Nav */}
      <PublicNav
        bandId={bandId}
        onHome={() => navigate("/")}
      />

      {/* Hero */}
      <section className="flex flex-col items-center px-6 pt-20 pb-16 text-center">
        <div className="mb-4 font-sans text-[0.75rem] tracking-[0.2em] uppercase text-brand-gold">
          Simple pricing
        </div>
        <h1 className="font-display text-[2.8rem] font-normal leading-[1.15] tracking-[-0.03em] text-text-primary mb-4 max-w-2xl">
          Plans for every stage
        </h1>
        <p className="text-text-secondary text-[1rem] leading-[1.7] max-w-lg mx-auto">
          Start free. Upgrade when your band is ready.
        </p>
      </section>

      {/* Pricing grid */}
      <div className="flex flex-wrap justify-center gap-5 w-full max-w-3xl mx-auto px-6 pb-24">
        {TIERS.map((tier) => {
          const isCurrent = currentTier === tier.key;
          const isDowngrade =
            (currentTier === "band" && tier.key !== "band") ||
            (currentTier === "solo" && tier.key === "free");

          return (
            <div
              key={tier.key}
              className={[
                "bg-surface rounded-sm p-9 flex flex-col flex-[1_1_240px] max-w-[280px]",
                isCurrent
                  ? "border-2 border-brand-gold"
                  : "border border-white/[0.08] border-t-2 border-t-white/[0.1]",
              ].join(" ")}
            >
              <div className="font-sans text-[0.7rem] tracking-[0.2em] uppercase text-brand-gold mb-3">
                {tier.label}
              </div>
              <div className="font-display text-[2.2rem] font-normal tracking-[-0.02em] text-text-primary mb-1">
                {tier.price}
              </div>
              <div className="text-[0.8rem] text-text-muted mb-6">
                {tier.priceNote}
              </div>
              <div className="border-t border-white/[0.08] mb-6" />
              <ul className="list-none p-0 mb-8 flex-1 space-y-2.5">
                {tier.features.map((f) => (
                  <li key={f} className="text-[0.88rem] text-text-secondary leading-[1.65] flex items-start gap-2">
                    <span className="text-brand-gold mt-0.5 shrink-0">—</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isCurrent ? (
                <div className="w-full text-center text-[0.78rem] tracking-[0.15em] uppercase text-brand-gold border border-brand-gold/20 rounded-sm py-3">
                  Current plan
                </div>
              ) : tier.key === "free" ? (
                /* Free tier — no upgrade CTA; show portal button for paid users */
                isDowngrade && bandId ? (
                  <button
                    onClick={() => setShowDowngradeModal(true)}
                    disabled={isLoading || isPortalLoading}
                    className="w-full bg-transparent text-text-primary border border-white/20 rounded-sm py-3 text-[0.78rem] tracking-[0.15em] uppercase hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPortalLoading ? "Opening billing…" : "Manage subscription"}
                  </button>
                ) : (
                  <div className="text-[0.8rem] text-text-muted text-center">
                    No credit card required
                  </div>
                )
              ) : isDowngrade && bandId ? (
                <button
                  onClick={handlePortalSession}
                  disabled={isLoading || isPortalLoading}
                  className="w-full bg-transparent text-text-primary border border-white/20 rounded-sm py-3 text-[0.78rem] tracking-[0.15em] uppercase hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPortalLoading ? "Opening billing…" : "Manage subscription"}
                </button>
              ) : bandId ? (
                <button
                  onClick={() => handleUpgrade(tier)}
                  disabled={isLoading}
                  className="w-full bg-brand-gold text-[#0d0d0c] font-bold border-0 rounded-sm py-3 text-[0.78rem] tracking-[0.15em] uppercase hover:bg-brand-gold/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingTier === tier.key ? "Redirecting…" : tier.ctaLabel}
                </button>
              ) : (
                <button
                  disabled={isLoading}
                  onClick={() => navigate("/signup")}
                  className="w-full bg-transparent text-text-primary border border-white/20 rounded-sm py-3 text-[0.78rem] tracking-[0.15em] uppercase hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tier.ctaLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <PublicFooter />

      <DowngradeConfirmModal
        open={showDowngradeModal}
        currentTier={currentTier}
        songCount={0}
        setlistCount={0}
        memberCount={0}
        onCancel={() => setShowDowngradeModal(false)}
        onConfirm={() => { setShowDowngradeModal(false); handlePortalSession(); }}
      />
    </div>
  );
}
